import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { isEmpty, uniqBy } from 'lodash';
import { createSelector } from 'reselect';
import i18n from 'i18next';
import {
  getEcapState,
  getOnboardingState,
  getAuthState,
  getTenantState,
} from '../../store/selectors';
import {
  getTenantDegrees,
  setEcap,
  saveEcap,
  getAllTenantHSClasses,
} from '../../store/actions';
import ClassesMainContent from './classes/ClassesMainContent';
import ClassesSubHeader from './classes/ClassesSubheader';
import ComponentHeader from './ComponentHeader';
import Loading from '../utility/Loading';
import { middle } from '../../copy/ecap/classesSetupCopy.json';
import {
  getCoreSubjectStrings,
  coreSubjectsEn,
  getAdditionalElectiveString,
  additionalElectiveEn,
  getAcademicYearTitles,
} from '../../copy/ecap/classesSetupStrings.js';
import {
  acadPlanClassMap,
  getCoreSubjectRequirements,
  getRequiredClassesBySubject,
  getRequiredClassesByAcadPlan,
  getSubjectObj,
  getSubjectId,
} from './classes/drag-drop/dragDropUtil';

class SetUpClasses extends Component {
  _isMounted = false;

  // DEV NOTE: This is just memoizing this computation so that we're not creating a new object every render
  // (and causing all those extra rerenders with our children when that happens)
  placedClassesMemoized = createSelector(
    /* eslint-disable no-unused-vars */
    (classesMatched, additionalElectives) => classesMatched,
    (classesMatched, additionalElectives) => additionalElectives,
    (classesMatched, additionalElectives) => {
      const obj = isEmpty(classesMatched)
        ? getSubjectObj().subjectSlotsObj
        : classesMatched;

      if (additionalElectives) {
        getAcademicYearTitles().forEach((year, yearIndex) => {
          obj[`${additionalElectiveEn}_${yearIndex}`] =
            obj[`${additionalElectiveEn}_${yearIndex}`] || [];
        });
      }

      return obj;
    }
  );

  constructor(props) {
    super(props);

    this.state = {
      tenantClasses: null,
      showAdditionalElectives: false,
    };
  }

  async componentDidMount() {
    this._isMounted = true;
    const { degreeChoices } = this.props.onboarding;
    const tenantClasses = await this.props.getAllTenantHSClasses(
      this.props.tenant.id,
      this.props.auth.jwtToken,
      [
        degreeChoices[1].acadPlan,
        degreeChoices[2].acadPlan,
        degreeChoices[3].acadPlan,
      ]
    );

    this.setState({
      tenantClasses,
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  // GETTERS

  get headerText() {
    return i18n.t('Set Up Your Classes');
  }

  get currentCareer() {
    return this.props.currentCareer;
  }

  get tenantClasses() {
    if (!this.state.tenantClasses) return [];

    return uniqBy(this.state.tenantClasses, 'requirement_title');
  }

  get placedClasses() {
    return this.placedClassesMemoized(
      this.props.ecap.plan.classes_matched,
      this.state.showAdditionalElectives
    );
  }

  get showAdditionalElectives() {
    let additionalElectivesFound = false;
    Object.keys(this.placedClasses).forEach((coreSubject) => {
      if (coreSubject.includes(`${additionalElectiveEn}`))
        additionalElectivesFound = true;
    });

    return this.state.showAdditionalElectives || additionalElectivesFound;
  }

  get coreSubjectsList() {
    const coreSubjects = Object.values(getCoreSubjectStrings());

    if (this.showAdditionalElectives)
      coreSubjects.push(getAdditionalElectiveString());

    return coreSubjects;
  }

  get coreSubjectsListEn() {
    const coreSubjects = Object.values(coreSubjectsEn);

    if (this.showAdditionalElectives) coreSubjects.push(additionalElectiveEn);

    return coreSubjects;
  }

  get classLists() {
    return {};
  }

  get coreSubjAreaIds() {
    const tempCoreSubjAreaIds = acadPlanClassMap(
      this.props.acadPlansArray,
      Object.values(coreSubjectsEn)
    );

    if (!isEmpty(this.classLists)) {
      this.props.acadPlansArray.forEach((acadPlan) => {
        const coreSubjectRequirements = getCoreSubjectRequirements(
          tempCoreSubjAreaIds,
          this.classLists,
          acadPlan
        );

        tempCoreSubjAreaIds[acadPlan] = coreSubjectRequirements[acadPlan];
      });
    }

    if (tempCoreSubjAreaIds && !isEmpty(tempCoreSubjAreaIds))
      return tempCoreSubjAreaIds;

    return [];
  }

  // get acadPlansArray() {
  //   return Object.values(this.props.onboarding.degreeChoices).map(
  //     (degree) => degree.acadPlan
  //   );
  // }

  get progressByDegree() {
    let obj = {};

    this.coreSubjectsListEn.forEach((subject) => {
      const requiredClasses = this.getRequiredClassesBySubjectParent(subject);

      if (!obj[subject]) {
        obj = {
          ...obj,
          ...requiredClasses,
          [subject]: requiredClasses[subject],
        };
      }
    });
    return obj;
  }

  // EVENTS

  getRequiredClassesBySubjectParent = (subject) => {
    const requiredClassesByAcadPlan = getRequiredClassesByAcadPlan(
      this.props.currentDegree.acadPlan,
      this.props.acadPlanClasses
    );
    const requiredClassesBySubject = getRequiredClassesBySubject(
      getSubjectId(subject),
      requiredClassesByAcadPlan
    );

    return { [subject]: requiredClassesBySubject, requiredClassesByAcadPlan };
  };

  addElectivesRow = () => {
    if (this._isMounted) this.setState({ showAdditionalElectives: true });
  };

  showDegreeRequirements = () => {
    this.props.sendShowDegreeToParent();
  };

  // RENDER

  render() {
    if (
      !this.props.currentDegree ||
      !this.currentCareer ||
      !this.tenantClasses.length ||
      !this.props.acadPlanClasses
    ) {
      return <Loading height="100vh" />;
    }

    const totalClassLength =
      this.classLists[this.props.currentDegree.acadPlan] &&
      this.classLists[this.props.currentDegree.acadPlan].length;

    return (
      <div id="set-up-classes">
        <ComponentHeader
          headerText={this.headerText}
          mainHeader
          tenant={this.props.tenant}
          appendText="setUpClasses"
        />
        <ClassesSubHeader
          career={this.currentCareer}
          degree={this.props.currentDegree}
          placedClasses={this.placedClasses}
          progressByDegree={this.progressByDegree}
          totalClassLength={totalClassLength}
          setCareerIndex={this.props.setCareerIndex}
          careerIndex={this.props.careerIndex}
        />
        <ClassesMainContent
          acadPlanClasses={this.props.acadPlanClasses}
          acadPlanReqClasses={this.coreSubjAreaIds}
          addElectivesRow={this.addElectivesRow}
          allClasses={this.state.tenantClasses}
          closeDegreeRequirements={this.props.closeDegreeRequirements}
          coreSubjectsList={this.coreSubjectsList}
          coreSubjectsListEn={this.coreSubjectsListEn}
          currentDegree={this.props.currentDegree}
          placedClasses={this.placedClasses}
          scrollNext={this.props.scrollNext}
          showDegreeRequirements={this.showDegreeRequirements}
          updateMobileSearchMode={this.props.updateMobileSearchMode}
          progressByDegree={this.progressByDegree}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const onboarding = getOnboardingState(state);
  const auth = getAuthState(state);
  const tenant = getTenantState(state);
  const ecap = getEcapState(state);

  return { onboarding, auth, tenant, ecap };
};

export default withRouter(
  connect(mapStateToProps, {
    getTenantDegrees,
    setEcap,
    saveEcap,
    getAllTenantHSClasses,
  })(SetUpClasses)
);
